import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY } from "@/lib/index";

const navLinks = [
  { href: "#leistungen", label: "Leistungen" },
  { href: "#social-media", label: "Social Media" },
  { href: "#vorteile", label: "Vorteile" },
  { href: "#ueber-uns", label: "Über uns" },
  { href: "#kontakt", label: "Kontakt" },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 35 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background/95 backdrop-blur-sm border-b border-border shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNavClick("#hero"); }}
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-primary-foreground text-lg group-hover:scale-110 transition-transform">
              KI
            </div>
            <div>
              <div className="font-heading font-bold text-foreground text-base leading-tight">
                KI Werbestudio
              </div>
              <div className="text-muted-foreground text-xs">Social Pro</div>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-primary transition-colors rounded-lg hover:bg-muted/50"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => handleNavClick("#kontakt")}
              className="px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-accent transition-all hover:scale-105 glow-orange"
            >
              Jetzt Kontakt
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menü"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden bg-card border-b border-border px-6 pb-6 overflow-hidden"
            >
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="flex items-center gap-2 w-full py-3 text-muted-foreground hover:text-primary transition-colors border-b border-border/50 last:border-0"
                >
                  <ChevronRight size={14} className="text-primary" />
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => handleNavClick("#kontakt")}
                className="mt-4 w-full px-5 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-accent transition-all"
              >
                Jetzt Kontakt aufnehmen
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* MAIN */}
      <main>{children}</main>

      {/* FOOTER */}
      <footer className="bg-card border-t border-border py-12 mt-0">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-primary-foreground text-lg">
                  KI
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground text-base">KI Werbestudio</div>
                  <div className="text-muted-foreground text-xs">Social Pro – Powered by pixnetmedia</div>
                </div>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
                {COMPANY.experience} Jahre europaweite Erfahrung. Kreative KI-Lösungen und professionelles Social-Media-Management aus einer Hand.
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-heading font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Navigation</h4>
              <ul className="space-y-2">
                {navLinks.map((l) => (
                  <li key={l.href}>
                    <button
                      onClick={() => { const el = document.querySelector(l.href); el?.scrollIntoView({ behavior: "smooth" }); }}
                      className="text-muted-foreground hover:text-primary text-sm transition-colors"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Kontakt</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>{COMPANY.address}</li>
                <li>
                  <a href={`tel:${COMPANY.phone}`} className="hover:text-primary transition-colors">
                    {COMPANY.phone}
                  </a>
                </li>
                <li>
                  <a href={`mailto:${COMPANY.email}`} className="hover:text-primary transition-colors">
                    {COMPANY.email}
                  </a>
                </li>
                <li className="text-xs pt-1 opacity-70">{COMPANY.openingHours}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-muted-foreground">
            <span>© 2026 KI Werbestudio · pixnetmedia · Mario Dudacy. Alle Rechte vorbehalten.</span>
            <div className="flex items-center gap-4">
              <Link
                to="/datenschutz"
                className="hover:text-primary transition-colors underline underline-offset-2"
              >
                Datenschutz
              </Link>
              <a href="https://www.pixnetmedia.de" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                www.pixnetmedia.de
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
